// Thin fetch wrapper centralizing the Ironlog backend protocol:
// - base URL (SYNC_URL) on every relative path
// - JSON body encode + accept
// - auth headers via AuthToken (X-Session-Token + X-Auth-Token)
// - timeout via AbortController
// - non-2xx → throws ApiError so callers can branch on status
//
// Consumers should prefer apiGet / apiPost / apiPut / apiDelete over raw fetch.
// The SyncQueue retry/offline path lives in utils/sync.js and intentionally
// stays on raw fetch — its retry semantics aren't a fit for this wrapper.

import { SYNC_URL } from "./sync";
import { AuthToken } from "./auth";

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const DEFAULT_TIMEOUT_MS = 15_000;

export async function apiFetch(path, opts = {}) {
  const {
    method = "GET",
    body,
    email,
    auth = true,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal: externalSignal,
    headers: extraHeaders = {},
  } = opts;

  const url = /^https?:/.test(path) ? path : `${SYNC_URL}${path}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  if (externalSignal) externalSignal.addEventListener("abort", onAbort);

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(auth ? AuthToken.getHeaders(email) : {}),
    ...extraHeaders,
  };

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body == null ? undefined : JSON.stringify(body),
      signal: ctrl.signal,
    });
  } catch (e) {
    if (e.name === "AbortError") {
      throw new ApiError("Request timed out", { status: 0 });
    }
    throw new ApiError(e.message || "Network error", { status: 0 });
  } finally {
    clearTimeout(timer);
    if (externalSignal) externalSignal.removeEventListener("abort", onAbort);
  }

  let parsed;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const msg = (parsed && parsed.error) || `Request failed with ${res.status}`;
    throw new ApiError(msg, { status: res.status, body: parsed });
  }
  return parsed;
}

export const apiGet = (path, opts) => apiFetch(path, { ...opts, method: "GET" });
export const apiPost = (path, body, opts) => apiFetch(path, { ...opts, method: "POST", body });
export const apiPut = (path, body, opts) => apiFetch(path, { ...opts, method: "PUT", body });
export const apiDelete = (path, opts) => apiFetch(path, { ...opts, method: "DELETE" });
